import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Toolbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CommonModal from "../component/common/commonModal";

const categories = ["Marketing", "Transactional", "Other"];

const parseNumbersFromText = (text) => {
  if (!text) return [];
  return text
    .split(/[\s,;]+/)
    .map((n) => n.trim())
    .filter((n) => n !== "");
};

const CreateContacts = () => {
  // ---- LIST PAGE STATE ----
  const [lists, setLists] = useState([
    // sample data – you can remove this and start from empty []
    {
      id: 110924,
      name: "anoop54",
      category: "Marketing",
      createdOn: "2025-11-28T11:28:00",
      lastUsed: "2025-11-28T11:28:00",
      numbers: ["9999999999", "8888888888", "7777777777", "6666666666"],
    },
    {
      id: 100071,
      name: "Truecaller_sampleData",
      category: "Marketing",
      createdOn: "2025-07-02T14:56:00",
      lastUsed: "2025-10-17T15:24:00",
      numbers: ["9999991111", "9999992222", "9999993333", "9999994444"],
    },
  ]);

  const [searchName, setSearchName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // ---- CREATE LIST DIALOG STATE ----
  const [createOpen, setCreateOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListCategory, setNewListCategory] = useState("");
  const [fileNumbers, setFileNumbers] = useState([]);
  const [typedText, setTypedText] = useState("");

  const typedNumbers = useMemo(
    () => parseNumbersFromText(typedText),
    [typedText]
  );
  const allPreviewNumbers = useMemo(
    () => [...fileNumbers, ...typedNumbers],
    [fileNumbers, typedNumbers]
  );

  // ---- FILTERED LISTS ----
  const filteredLists = useMemo(() => {
    return lists.filter((list) => {
      const matchesName = list.name
        .toLowerCase()
        .includes(searchName.toLowerCase());

      const matchesCategory = filterCategory
        ? list.category === filterCategory
        : true;

      const createdDate = new Date(list.createdOn);
      const matchesStart = filterStartDate
        ? createdDate >= new Date(filterStartDate)
        : true;
      const matchesEnd = filterEndDate
        ? createdDate <= new Date(filterEndDate)
        : true;

      return matchesName && matchesCategory && matchesStart && matchesEnd;
    });
  }, [lists, searchName, filterCategory, filterStartDate, filterEndDate]);

  const anySelected = selectedIds.length > 0;

  // ---- HANDLERS: LIST PAGE ----
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredLists.map((l) => l.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDownload = () => {
    if (!anySelected) return;

    // For simplicity, we'll download numbers for the FIRST selected list.
    const firstId = selectedIds[0];
    const list = lists.find((l) => l.id === firstId);
    if (!list) return;

    const content = list.numbers.join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${list.name || "list"}-${list.id}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDelete = () => {
    if (!anySelected) return;
    setLists((prev) => prev.filter((l) => !selectedIds.includes(l.id)));
    setSelectedIds([]);
  };

  // ---- HANDLERS: CREATE LIST DIALOG ----
  const resetCreateForm = () => {
    setNewListName("");
    setNewListCategory("");
    setFileNumbers([]);
    setTypedText("");
  };

  const openCreate = () => {
    resetCreateForm();
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || "";
      const nums = parseNumbersFromText(text.toString());
      setFileNumbers(nums);
    };
    reader.readAsText(file);
  };

  const handleCreateSubmit = () => {
    if (!newListName || !newListCategory || allPreviewNumbers.length === 0) {
      alert("Please fill all fields and add some numbers.");
      return;
    }

    const newList = {
      id: Date.now(), // simple unique id
      name: newListName,
      category: newListCategory,
      createdOn: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
      numbers: allPreviewNumbers,
    };

    setLists((prev) => [newList, ...prev]);
    closeCreate();
  };

  const isEmpty = lists.length === 0;

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        Contacts Lists
      </Typography>

      <Paper elevation={1}>
        <Toolbar
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            minHeight: 36,
            paddingY: 0.5,
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            label="Search"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            sx={{
              width: 150,
              "& .MuiInputBase-root": {
                height: 34,
                backgroundColor: "#fff",
              },
            }}
          />

          {/* Category */}
          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              "& .MuiInputBase-root": { height: 34, backgroundColor: "#fff" },
            }}
          >
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Start Date */}
          <TextField
            size="small"
            type="date"
            label="Start"
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 140,
              "& .MuiInputBase-root": { height: 34, backgroundColor: "#fff" },
            }}
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
          />

          {/* End Date */}
          <TextField
            size="small"
            type="date"
            label="End"
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 140,
              "& .MuiInputBase-root": { height: 34, backgroundColor: "#fff" },
            }}
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
          />

          <Box flexGrow={1} />

          {/* Download */}
          <Tooltip title="Download">
            <span>
              <IconButton
                onClick={handleDownload}
                disabled={!anySelected}
                color="primary"
                sx={{ p: "3px" }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Delete */}
          <Tooltip title="Delete">
            <span>
              <IconButton
                onClick={handleDelete}
                disabled={!anySelected}
                color="error"
                sx={{ p: "3px" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Create Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={openCreate}
            sx={{
              height: 32,
              paddingX: 1.2,
              fontSize: "0.75rem",
              minWidth: 100,
            }}
          >
            Create
          </Button>
        </Toolbar>
      </Paper>

      {/* LIST TABLE OR EMPTY STATE */}
      <Box mt={2}>
        {isEmpty ? (
          <Paper
            elevation={1}
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: "1rem", fontWeight: 600 }} gutterBottom>
              No Lists Found
            </Typography>

            <Typography
              sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 3 }}
            >
              Click "Create List" to upload or add numbers.
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ textTransform: "none", fontSize: "0.85rem" }}
            >
              Create List
            </Button>
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredLists.length
                      }
                      checked={
                        filteredLists.length > 0 &&
                        selectedIds.length === filteredLists.length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    List ID
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Created On
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Last Used
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                  >
                    Count
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredLists.map((list) => {
                  const isChecked = selectedIds.includes(list.id);

                  return (
                    <TableRow
                      key={list.id}
                      hover
                      selected={isChecked}
                      sx={{
                        cursor: "pointer",
                        "& td": { fontSize: "0.82rem" },
                      }}
                      onClick={() => handleToggleSelect(list.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isChecked} />
                      </TableCell>

                      <TableCell>{list.id}</TableCell>
                      <TableCell>{list.name}</TableCell>
                      <TableCell>{list.category}</TableCell>

                      <TableCell>
                        {new Date(list.createdOn).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {new Date(list.lastUsed).toLocaleDateString()}
                      </TableCell>

                      <TableCell align="right">{list.numbers.length}</TableCell>
                    </TableRow>
                  );
                })}

                {filteredLists.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography
                        sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                      >
                        No lists match your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Create List
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {/* List Name & Category */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="List Name"
              variant="outlined"
              fullWidth
              size="small"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              sx={{
                "& .MuiInputBase-input": { fontSize: "0.85rem" },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
              }}
            />

            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "0.85rem" }}>
                List Category
              </InputLabel>
              <Select
                label="List Category"
                value={newListCategory}
                onChange={(e) => setNewListCategory(e.target.value)}
                sx={{
                  "& .MuiSelect-select": { fontSize: "0.85rem" },
                  backgroundColor: "#fff",
                }}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c} sx={{ fontSize: "0.85rem" }}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Upload Section */}
          <Box mt={2}>
            <Typography sx={{ fontSize: "0.85rem", mb: 0.5, fontWeight: 500 }}>
              Upload Data
            </Typography>

            <Button
              variant="outlined"
              component="label"
              size="small"
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
            >
              Upload File (.txt / .csv)
              <input
                type="file"
                hidden
                accept=".txt,.csv"
                onChange={handleFileChange}
              />
            </Button>

            {fileNumbers.length > 0 && (
              <Typography
                variant="caption"
                sx={{ mt: 0.5, display: "block", fontSize: "0.75rem" }}
              >
                Parsed {fileNumbers.length} numbers from file.
              </Typography>
            )}
          </Box>

          {/* Side-by-side: Type Input + Preview */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2}>
            {/* Left: Type | Paste */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", mb: 0.5 }}>
                Type / Copy-Paste
              </Typography>

              <TextField
                multiline
                minRows={10}
                fullWidth
                size="small"
                placeholder="Type or paste numbers..."
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                sx={{
                  "& textarea": { fontSize: "0.85rem" },
                  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                }}
              />
            </Box>

            {/* Right: Preview */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", mb: 0.5 }}>
                Preview List ({allPreviewNumbers.length})
              </Typography>

              <TextField
                multiline
                minRows={10}
                fullWidth
                size="small"
                value={allPreviewNumbers.join("\n")}
                InputProps={{ readOnly: true }}
                sx={{
                  "& textarea": { fontSize: "0.85rem" },
                  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            sx={{ fontSize: "0.8rem", textTransform: "none" }}
          >
            Save List
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={closeCreate}
            sx={{ fontSize: "0.8rem", textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateContacts;
